import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { Heart, TrendingDown } from 'lucide-react'
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, TimeScale } from 'chart.js'
import { Line } from 'react-chartjs-2'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import 'chartjs-adapter-date-fns'
import { RunningShoe } from '../types'
import { getShoeById, checkFavoriteStatus, addFavorite, removeFavorite, getPriceHistory, checkBuyNowPermission } from '../utils/api'
import { useAuth } from '../contexts/AuthContext'
import QuickAuthModal from '../components/QuickAuthModal'
import PremiumUpgradeModal from '../components/PremiumUpgradeModal'
import toast from 'react-hot-toast'

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, TimeScale, ChartDataLabels)

interface PriceHistoryDataPoint {
  date: Date
  menPrice: number | null
  womenPrice: number | null
  originalPrice: number
}

const ShoePage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuth()
  const [shoe, setShoe] = useState<RunningShoe | null>(null)
  const [priceHistory, setPriceHistory] = useState<PriceHistoryDataPoint[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isMenFavorite, setIsMenFavorite] = useState(false)
  const [isWomenFavorite, setIsWomenFavorite] = useState(false)
  const [favoriteLoading, setFavoriteLoading] = useState<'men' | 'women' | null>(null)
  const [timeRange, setTimeRange] = useState<'15d' | '1m' | '3m' | '1y'>('1m')
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [showPremiumModal, setShowPremiumModal] = useState(false)
  const [pendingPurchaseUrl, setPendingPurchaseUrl] = useState<string | null>(null)
  const [hasPermission, setHasPermission] = useState<boolean>(false)

  // Check Buy Now permission on mount
  useEffect(() => {
    const checkPermission = async () => {
      try {
        const permission = await checkBuyNowPermission();
        setHasPermission(permission.hasPermission);
      } catch (error) {
        setHasPermission(false);
      }
    };
    checkPermission();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!id) {
      navigate('/')
      return
    }

    const fetchShoe = async () => {
      try {
        const response = await getShoeById(id)
        if (response.success && response.data) {
          setShoe(response.data.shoe)
          // Fetch real price history data for both men and women
          await fetchPriceHistory(id, timeRange)
        } else {
          setError('Shoe not found')
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load shoe details')
      } finally {
        setLoading(false)
      }
    }

    fetchShoe()
  }, [id, navigate])

  // Check favorite status for both men's and women's versions
  useEffect(() => {
    if (!id || !isAuthenticated) {
      setIsMenFavorite(false)
      setIsWomenFavorite(false)
      return
    }

    const checkFavorites = async () => {
      try {
        // Check men's version
        const menResult = await checkFavoriteStatus(`${id}-men`)
        setIsMenFavorite(menResult.isFavorite)

        // Check women's version
        const womenResult = await checkFavoriteStatus(`${id}-women`)
        setIsWomenFavorite(womenResult.isFavorite)
      } catch (err) {
        // Silently fail - favorite status is not critical
        console.error('Failed to check favorite status:', err)
      }
    }

    checkFavorites()
  }, [id, isAuthenticated])

  const fetchPriceHistory = async (shoeId: string, period: '15d' | '1m' | '3m' | '1y') => {
    try {
      // Fetch both men's and women's price history
      const [menResponse, womenResponse] = await Promise.all([
        getPriceHistory(shoeId, period, 'men'),
        getPriceHistory(shoeId, period, 'women')
      ])

      if (menResponse.success && menResponse.data) {
        // Merge men's and women's data by date
        const dateMap = new Map<string, PriceHistoryDataPoint>()

        // Add men's prices
        menResponse.data.forEach(entry => {
          const dateKey = new Date(entry.date).toISOString()
          dateMap.set(dateKey, {
            date: new Date(entry.date),
            menPrice: entry.price,
            womenPrice: null,
            originalPrice: entry.originalPrice || 0
          })
        })

        // Add women's prices
        if (womenResponse.success && womenResponse.data) {
          womenResponse.data.forEach(entry => {
            const dateKey = new Date(entry.date).toISOString()
            const existing = dateMap.get(dateKey)
            if (existing) {
              existing.womenPrice = entry.price
            } else {
              dateMap.set(dateKey, {
                date: new Date(entry.date),
                menPrice: null,
                womenPrice: entry.price,
                originalPrice: entry.originalPrice || 0
              })
            }
          })
        }

        // Convert map to array and sort by date
        const mergedData = Array.from(dateMap.values()).sort((a, b) => a.date.getTime() - b.date.getTime())
        setPriceHistory(mergedData)
      }
    } catch (err) {
      console.error('Failed to fetch price history:', err)
      // Fall back to showing at least current price if available
      if (shoe) {
        setPriceHistory([{
          date: new Date(),
          menPrice: shoe.price?.current || 0,
          womenPrice: shoe.price?.womenCurrent || 0,
          originalPrice: shoe.price?.original || 0
        }])
      }
    }
  }

  // Update price history when time range changes
  useEffect(() => {
    if (shoe && id) {
      fetchPriceHistory(id, timeRange)
    }
  }, [timeRange, shoe, id])

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price)
  }

  const calculateDiscount = (current: number, original: number) => {
    return Math.round(((original - current) / original) * 100)
  }

  const handleFavoriteClick = async (gender: 'men' | 'women') => {
    if (!isAuthenticated) {
      // Show authentication modal for unauthenticated users
      setShowAuthModal(true)
      return
    }

    if (!shoe || !id) return

    // Prevent multiple clicks
    if (favoriteLoading) return

    setFavoriteLoading(gender)

    const isFavorite = gender === 'men' ? isMenFavorite : isWomenFavorite
    const setIsFavorite = gender === 'men' ? setIsMenFavorite : setIsWomenFavorite
    const shoeId = `${id}-${gender}`
    const genderLabel = gender === 'men' ? "Men's" : "Women's"

    try {
      if (isFavorite) {
        // Remove from favorites
        await removeFavorite(shoeId)
        setIsFavorite(false)
        toast.success(`Removed ${genderLabel} ${shoe.brand} ${shoe.model} from favorites`)
      } else {
        // Add to favorites
        await addFavorite({
          shoeId: shoeId,
          shoeName: `${shoe.brand} ${shoe.model}`,  // 不包含 Men's/Women's 前缀
          shoeBrand: gender === 'men' ? 'Men' : 'Women',  // 用性别代替品牌
          shoeImage: shoe.images[0] || '',
          shoeUrl: shoe.availability?.url || '',
          currentPrice: gender === 'men' ? shoe.price?.current : shoe.price?.current * 0.75, // Mock women's price
          originalPrice: shoe.price?.original,
          discountPercentage: shoe.price?.discount
        })
        setIsFavorite(true)
        toast.success(`Added ${genderLabel} ${shoe.brand} ${shoe.model} to favorites`)
      }
    } catch (err: any) {
      console.error('Failed to update favorite:', err)
      toast.error(err.message || 'Failed to update favorites')
    } finally {
      setFavoriteLoading(null)
    }
  }

  const handleBuyNow = async (url: string) => {
    // Validate purchase URL
    if (!url || url === '' || url === '#') {
      toast.error('Purchase link is not available for this shoe.');
      return;
    }

    // Check permission via API
    try {
      const permission = await checkBuyNowPermission();

      if (permission.hasPermission) {
        // User is Premium - open link directly
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }

      if (permission.needsAuth) {
        // User not logged in - show auth modal
        setPendingPurchaseUrl(url);
        setShowAuthModal(true);
        return;
      }

      if (permission.needsPayment) {
        // User logged in but not Premium - show premium upgrade modal
        setPendingPurchaseUrl(url);
        setShowPremiumModal(true);
        return;
      }
    } catch (error) {
      console.error('Error checking buy now permission:', error);
      toast.error('Failed to check permissions. Please try again.');
    }
  }

  const handleAuthSuccess = async () => {
    setShowAuthModal(false);
    toast.success('Successfully signed in!');
    
    // After successful auth, check if user now has premium
    try {
      const permission = await checkBuyNowPermission();
      if (permission.hasPermission && pendingPurchaseUrl) {
        // User became premium after auth - open link
        window.open(pendingPurchaseUrl, '_blank', 'noopener,noreferrer');
        setPendingPurchaseUrl(null);
      } else if (permission.needsPayment && pendingPurchaseUrl) {
        // User logged in but still needs premium - show upgrade modal
        setShowPremiumModal(true);
      }
    } catch (error) {
      console.error('Error re-checking permissions:', error);
    }
  }

  const handleAuthClose = () => {
    setShowAuthModal(false)
  }

  // Calculate dynamic Y-axis range based on actual data
  const allPrices = priceHistory.flatMap(p => [p.menPrice, p.womenPrice].filter(Boolean))
  const minPrice = allPrices.length > 0 ? Math.min(...allPrices) : 0
  const maxPrice = allPrices.length > 0 ? Math.max(...allPrices) : 200
  const yAxisMin = Math.floor(minPrice * 0.9 / 10) * 10
  const yAxisMax = Math.ceil(maxPrice * 1.1 / 10) * 10

  // Find indices for Current (last), Lowest, and Highest prices for Men's
  // IMPORTANT: Use indices from the original priceHistory array, not a filtered array
  const menPrices = priceHistory.map(p => p.menPrice).filter(p => p !== null) as number[]
  const menLowestPrice = menPrices.length > 0 ? Math.min(...menPrices) : 0
  const menHighestPrice = menPrices.length > 0 ? Math.max(...menPrices) : 0

  // Find the indices in the ORIGINAL priceHistory array (not filtered)
  // Current: last non-null entry
  let menCurrentIndex = -1
  for (let i = priceHistory.length - 1; i >= 0; i--) {
    if (priceHistory[i].menPrice !== null) {
      menCurrentIndex = i
      break
    }
  }
  const menCurrentPrice = menCurrentIndex >= 0 ? priceHistory[menCurrentIndex].menPrice as number : 0

  // Lowest: last occurrence of lowest price
  let menLowestIndex = -1
  for (let i = priceHistory.length - 1; i >= 0; i--) {
    if (priceHistory[i].menPrice === menLowestPrice) {
      menLowestIndex = i
      break
    }
  }

  // Highest: last occurrence of highest price
  let menHighestIndex = -1
  for (let i = priceHistory.length - 1; i >= 0; i--) {
    if (priceHistory[i].menPrice === menHighestPrice) {
      menHighestIndex = i
      break
    }
  }

  // Find indices for Current (last), Lowest, and Highest prices for Women's
  const womenPrices = priceHistory.map(p => p.womenPrice).filter(p => p !== null) as number[]
  const womenLowestPrice = womenPrices.length > 0 ? Math.min(...womenPrices) : 0
  const womenHighestPrice = womenPrices.length > 0 ? Math.max(...womenPrices) : 0

  // Find the indices in the ORIGINAL priceHistory array (not filtered)
  // Current: last non-null entry
  let womenCurrentIndex = -1
  for (let i = priceHistory.length - 1; i >= 0; i--) {
    if (priceHistory[i].womenPrice !== null) {
      womenCurrentIndex = i
      break
    }
  }
  const womenCurrentPrice = womenCurrentIndex >= 0 ? priceHistory[womenCurrentIndex].womenPrice as number : 0

  // Lowest: last occurrence of lowest price
  let womenLowestIndex = -1
  for (let i = priceHistory.length - 1; i >= 0; i--) {
    if (priceHistory[i].womenPrice === womenLowestPrice) {
      womenLowestIndex = i
      break
    }
  }

  // Highest: last occurrence of highest price
  let womenHighestIndex = -1
  for (let i = priceHistory.length - 1; i >= 0; i--) {
    if (priceHistory[i].womenPrice === womenHighestPrice) {
      womenHighestIndex = i
      break
    }
  }

  // Calculate statistics for display (using men's prices)
  const currentPrice = menPrices.length > 0 ? menCurrentPrice : 0
  const lowestPrice = menPrices.length > 0 ? menLowestPrice : 0
  const averagePrice = menPrices.length > 0 ? menPrices.reduce((a, b) => a + b, 0) / menPrices.length : 0

  // Chart configuration
  const chartData = {
    datasets: [
      {
        label: "Men's",
        data: priceHistory.map(p => ({ x: p.date, y: p.menPrice })),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.05)',
        borderWidth: 2,
        tension: 0.3,
        fill: true,
        pointRadius: 2,
        pointHoverRadius: 5,
        pointBackgroundColor: 'rgb(59, 130, 246)',
        datalabels: {
          display: (context: any) => {
            const index = context.dataIndex
            // Only show labels for current, lowest, and highest prices
            return index === menCurrentIndex || index === menLowestIndex || index === menHighestIndex
          },
          formatter: (value: any, context: any) => {
            const index = context.dataIndex
            if (index === menCurrentIndex) return `Current\n$${Math.round(value.y)}`
            if (index === menLowestIndex) return `Lowest\n$${Math.round(value.y)}`
            if (index === menHighestIndex) return `Highest\n$${Math.round(value.y)}`
            return ''
          },
          color: 'rgb(29, 78, 216)', // Darker blue for men's labels
          backgroundColor: 'transparent',
          borderWidth: 0,
          padding: 4,
          font: {
            size: 11,
            weight: 'bold' as const
          },
          align: (context: any) => {
            const index = context.dataIndex
            // Lowest always at bottom, Current and Highest at top
            return index === menLowestIndex ? 'bottom' : 'top'
          },
          offset: 8
        }
      },
      {
        label: "Women's",
        data: priceHistory.map(p => ({ x: p.date, y: p.womenPrice })),
        borderColor: 'rgb(236, 72, 153)',
        backgroundColor: 'rgba(236, 72, 153, 0.05)',
        borderWidth: 2,
        tension: 0.3,
        fill: true,
        pointRadius: 2,
        pointHoverRadius: 5,
        pointBackgroundColor: 'rgb(236, 72, 153)',
        datalabels: {
          display: (context: any) => {
            const index = context.dataIndex
            // Only show labels for current, lowest, and highest prices
            return index === womenCurrentIndex || index === womenLowestIndex || index === womenHighestIndex
          },
          formatter: (value: any, context: any) => {
            const index = context.dataIndex
            if (index === womenCurrentIndex) return `Current\n$${Math.round(value.y)}`
            if (index === womenLowestIndex) return `Lowest\n$${Math.round(value.y)}`
            if (index === womenHighestIndex) return `Highest\n$${Math.round(value.y)}`
            return ''
          },
          color: 'rgb(190, 24, 93)', // Darker pink for women's labels
          backgroundColor: 'transparent',
          borderWidth: 0,
          padding: 4,
          font: {
            size: 11,
            weight: 'bold' as const
          },
          align: (context: any) => {
            const index = context.dataIndex
            // Lowest always at bottom, Current and Highest at top
            return index === womenLowestIndex ? 'bottom' : 'top'
          },
          offset: 8
        }
      }
    ]
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 10,
        titleFont: {
          size: 12
        },
        bodyFont: {
          size: 14,
          weight: 'bold' as const
        },
        callbacks: {
          title: function (context: any) {
            // Format date using UTC to avoid timezone issues
            const date = new Date(context[0].parsed.x)
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
            const month = months[date.getUTCMonth()]
            const day = date.getUTCDate()
            const year = date.getUTCFullYear()
            return `${month} ${day}, ${year}`
          },
          label: function (context: any) {
            return context.dataset.label + ': $' + Math.round(context.parsed.y)
          }
        }
      },
      datalabels: {
        display: false // Disable by default, enable per dataset
      }
    },
    scales: {
      y: {
        position: 'left' as const,
        beginAtZero: false,
        min: yAxisMin,
        max: yAxisMax,
        ticks: {
          callback: function (value: any) {
            return '$' + value
          },
          font: {
            size: 11
          },
          stepSize: 10
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false
        }
      },
      y1: {
        position: 'right' as const,
        beginAtZero: false,
        min: yAxisMin,
        max: yAxisMax,
        ticks: {
          callback: function (value: any) {
            return '$' + value
          },
          font: {
            size: 11
          },
          stepSize: 10
        },
        grid: {
          display: false,
          drawBorder: false
        }
      },
      x: {
        type: 'time' as const,
        time: {
          unit: (timeRange === '15d' || timeRange === '1m' ? 'day' : timeRange === '3m' ? 'week' : 'month') as 'day' | 'week' | 'month',
          displayFormats: {
            day: 'M/d',
            week: 'M/d',
            month: 'MMM'
          },
          tooltipFormat: 'MMM d, yyyy',
          // Force UTC timezone interpretation
          isoWeekday: false
        },
        // Set min/max to actual data range to avoid showing empty periods
        min: priceHistory.length > 0 ? priceHistory[0].date.getTime() : undefined,
        max: priceHistory.length > 0 ? priceHistory[priceHistory.length - 1].date.getTime() : undefined,
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          font: {
            size: 10
          },
          maxTicksLimit: 12,
          // Format ticks to use UTC
          callback: function (value: any, index: number, ticks: any) {
            const date = new Date(value)
            const utcMonth = date.getUTCMonth() + 1
            const utcDay = date.getUTCDate()
            return `${utcMonth}/${utcDay}`
          }
        },
        grid: {
          display: false
        }
      }
    }
  }

  // Clean shoe name: remove "Men's" or "Women's" suffix (moved up for early SEO)
  const cleanShoeName = (name: string) => {
    return name.replace(/\s+(Men's|Women's)$/i, '')
  }

  // SEO metadata - 优化版本（移到早期，确保在 loading 状态下也能生成）
  // 生成优化后的标题（包含折扣百分比和年份）
  const generateOptimizedPageTitle = () => {
    if (!shoe) return 'Loading... | ShoeSaver'
    const productName = cleanShoeName(shoe.name)
    const currentPrice = shoe.price.current
    const originalPrice = shoe.price.original
    
    let title = productName
    
    // 添加价格
    if (currentPrice > 0) {
      title += ` - $${Math.round(currentPrice)}`
      
      // 添加折扣百分比
      if (originalPrice && currentPrice < originalPrice) {
        const discount = Math.round((1 - currentPrice / originalPrice) * 100)
        if (discount >= 10) {
          title += ` (${discount}% Off)`
        }
      }
    }
    
    // 添加年份和品牌
    const year = new Date().getFullYear()
    title += ` | Lowest Price ${year} | ShoeSaver`
    
    // 确保标题不超过 60 字符
    if (title.length > 60) {
      title = `${productName} - $${Math.round(currentPrice)} | ShoeSaver`
    }
    
    return title
  }
  
  // 生成优化后的描述（突出价格优势和行动号召）
  const generateOptimizedPageDescription = () => {
    if (!shoe) return 'Find the best prices on running shoes at ShoeSaver. Compare 650+ running shoes from Nike, HOKA, Adidas, Brooks & more.'
    const productName = cleanShoeName(shoe.name)
    const currentPrice = shoe.price.current
    const originalPrice = shoe.price.original
    
    // 计算折扣
    let discountText = ''
    if (originalPrice && currentPrice < originalPrice) {
      const discount = Math.round((1 - currentPrice / originalPrice) * 100)
      if (discount >= 10) {
        discountText = ` (${discount}% OFF)`
      }
    }
    
    // 构建描述
    let description = ''
    
    // 价格信息（开头最重要）
    if (currentPrice > 0) {
      description = `${productName} at $${Math.round(currentPrice)}${discountText} - Lowest price guaranteed.`
    } else {
      description = `Find the best price for ${productName} running shoes.`
    }
    
    // 添加产品特性
    if (shoe.features && shoe.features.length > 0) {
      description += ` ${shoe.features.slice(0, 2).join(' & ')}.`
    } else if (shoe.detailedSpecs?.pace) {
      description += ` Perfect for ${shoe.detailedSpecs.pace.toLowerCase()}.`
    }
    
    // 添加评分（如果有）
    if (shoe.ratings?.overall) {
      description += ` ${Math.round(shoe.ratings.overall * 20)}/100 expert rating.`
  }

    // 添加行动号召
    description += ' Free price tracking. Compare retailers & save today!'
    
    // 确保描述不超过 155 字符
    if (description.length > 155) {
      description = description.substring(0, 152) + '...'
    }
    
    return description
  }
  
  // 生成 SEO 关键词（10-15个）
  const generatePageKeywords = () => {
    if (!shoe) return 'running shoes, lowest price running shoes, cheap running shoes, running shoe deals'
    const productName = cleanShoeName(shoe.name)
    const brand = shoe.brand
    
    const keywords: string[] = [
      // 核心关键词
      productName,
      `${productName} running shoes`,
      // 价格关键词
      `${productName} lowest price`,
      `buy ${productName} cheap`,
      `${productName} on sale`,
      `${productName} discount`,
      `${productName} deal`,
      // 比较关键词
      `${productName} price comparison`,
      `best price ${productName}`,
      // 品牌关键词
      `${brand} running shoes`,
      `${brand} shoes sale`,
    ]
    
    // 性别关键词
    if (shoe.price.current) {
      keywords.push(`${productName} men`)
    }
    if (shoe.price.womenCurrent) {
      keywords.push(`${productName} women`)
    }
    
    // 用途关键词
    if (shoe.detailedSpecs?.pace) {
      keywords.push(`${productName} for ${shoe.detailedSpecs.pace.toLowerCase()}`)
      keywords.push(`${shoe.detailedSpecs.pace.toLowerCase()} running shoes`)
    }
    
    // 特性关键词
    if (shoe.specifications?.cushioning) {
      keywords.push(`${shoe.specifications.cushioning} cushioning running shoes`)
    }
    if (shoe.specifications?.stability) {
      keywords.push(`${shoe.specifications.stability} running shoes`)
    }
    
    // 去重并返回
    return [...new Set(keywords)].slice(0, 15).join(', ')
  }

  // 提前计算 SEO 数据（在 loading 检查之前）
  const pageTitle = generateOptimizedPageTitle()
  const pageDescription = generateOptimizedPageDescription()
  const pageKeywords = generatePageKeywords()
  const pageImage = shoe?.images && shoe.images.length > 0 ? shoe.images[0] : '/shoe-placeholder.png'
  const pageUrl = typeof window !== 'undefined' ? window.location.href : ''

  // 强制更新 document.title 作为 React Helmet 的备选方案
  // 这确保了即使 Helmet 有问题，标题也能正确显示
  useEffect(() => {
    if (pageTitle && pageTitle !== 'Loading... | ShoeSaver') {
      document.title = pageTitle
      // 同时更新 meta 标签
      const descMeta = document.querySelector('meta[name="description"]')
      const keywordsMeta = document.querySelector('meta[name="keywords"]')
      if (descMeta) descMeta.setAttribute('content', pageDescription)
      if (keywordsMeta) keywordsMeta.setAttribute('content', pageKeywords)
    }
  }, [pageTitle, pageDescription, pageKeywords])

  // Structured data for rich snippets (只在有数据时生成)
  const ratingValue = shoe?.ratings?.overall ? Math.min(5, Math.max(1, shoe.ratings.overall)) : null;
  
  const structuredData = shoe ? {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": cleanShoeName(shoe.name),
    "image": pageImage,
    "description": pageDescription,
    "brand": {
      "@type": "Brand",
      "name": shoe.brand
    },
    "sku": shoe._id,
    "offers": {
      "@type": "Offer",
      "url": shoe.availability.url,
      "priceCurrency": "USD",
      "price": shoe.price.current,
      "availability": "https://schema.org/InStock",
      "priceValidUntil": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],  // 始终添加，解决 Google 警告
      "seller": {
        "@type": "Organization",
        "name": "ShoeSaver"
      }
    },
    "aggregateRating": ratingValue ? {
      "@type": "AggregateRating",
      "ratingValue": ratingValue.toFixed(1),
      "bestRating": 5,
      "worstRating": 1,
      "ratingCount": shoe.reviews?.count || 100,
      "reviewCount": shoe.reviews?.count || 100
    } : undefined
  } : null;

  // SEO Helmet 内容 - 直接内联渲染，避免在组件内部定义组件
  const seoHelmetContent = (
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{pageTitle}</title>
        <meta name="title" content={pageTitle} />
        <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="product" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={pageImage} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={pageDescription} />
        <meta property="twitter:image" content={pageImage} />

        {/* Canonical URL */}
        <link rel="canonical" href={pageUrl} />

        {/* Structured Data */}
        {structuredData && (
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        )}
      </Helmet>
  );

  if (loading) {
    return (
      <>
        {seoHelmetContent}
        <div className="min-h-screen bg-white sm:bg-gradient-to-br sm:from-orange-50 sm:to-blue-50 flex items-center justify-center">
          <div className="loading-spinner"></div>
        </div>
      </>
    )
  }

  if (error || !shoe) {
    return (
      <>
        {seoHelmetContent}
        <div className="min-h-screen bg-white sm:bg-gradient-to-br sm:from-orange-50 sm:to-blue-50 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {error || 'Shoe Not Found'}
            </h2>
            <p className="text-gray-600 mb-6">
              We couldn't find the shoe you're looking for.
            </p>
            <button
              onClick={() => navigate('/')}
              className="btn-primary"
            >
              Back to Home
            </button>
          </div>
        </div>
      </>
    )
  }

  // Get time range label for stats
  const getTimeRangeLabel = () => {
    switch (timeRange) {
      case '15d': return '15d'
      case '1m': return '1m'
      case '3m': return '3m'
      case '1y': return '1y'
    }
  }

  return (
    <>
      {seoHelmetContent}

      <div className="min-h-screen bg-white sm:bg-gradient-to-br sm:from-orange-50 sm:to-blue-50">
        <main className="w-full mx-auto px-2 sm:px-6 lg:px-8 py-2 sm:py-4">

          {/* Product Hero Section */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-2.5 sm:p-6 mb-3 sm:mb-6 border border-gray-100">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-6">

              {/* Left Side: Picture + Features (2 columns) */}
              <div className="lg:col-span-2">
                <div className="flex flex-row md:flex-row gap-3 sm:gap-6 items-start">

                  {/* Shoe Picture */}
                  <div className="flex-shrink-0 w-28 sm:w-full md:w-64">
                    {shoe.images && shoe.images.length > 0 ? (
                      <img
                        src={shoe.images[0]}
                        alt={cleanShoeName(shoe.name)}
                        className="w-full h-auto rounded-lg shadow-md bg-gray-100"
                      />
                    ) : (
                      <div className="w-full aspect-square rounded-lg shadow-md bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                        <div className="text-center p-6">
                          <div className="text-6xl mb-4">👟</div>
                          <div className="text-xl font-bold text-gray-700">{shoe.brand}</div>
                          <div className="text-sm text-gray-500 mt-2">Image Coming Soon</div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Title + Key Features */}
                  <div className="flex flex-col flex-1 min-w-0">
                    {/* Product Name and Rating */}
                    <div className="mb-2 sm:mb-4">
                      <h1 className="text-base sm:text-2xl font-bold text-gray-900 mb-0.5 sm:mb-1 leading-tight">{cleanShoeName(shoe.name)}</h1>
                      <div className="text-xs sm:text-base text-gray-500">
                        ⭐ {shoe.ratings?.overall ? Math.round(shoe.ratings.overall * 20) : 'N/A'}
                      </div>
                    </div>

                    {/* Key Features (5 bullet points from auto-generated features) */}
                    <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm text-gray-700 hidden sm:block">
                      {shoe.features && shoe.features.length > 0 ? (
                        // Use auto-generated features if available
                        shoe.features.map((feature, index) => (
                          <div key={index} className="flex items-start gap-2">
                            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 flex-shrink-0"></span>
                            <span>{feature}</span>
                          </div>
                        ))
                      ) : (
                        // Fallback to old logic if features not generated yet
                        <>
                          {shoe.specifications.cushioning && (
                            <div className="flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                              <span className="capitalize">{shoe.specifications.cushioning} Cushioning</span>
                            </div>
                          )}
                          {shoe.specifications.stability && (
                            <div className="flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                              <span className="capitalize">{shoe.specifications.stability}</span>
                            </div>
                          )}
                          {shoe.specifications.weight && (
                            <div className="flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                              <span>Weight: {shoe.specifications.weight} oz</span>
                            </div>
                          )}
                          {shoe.specifications.drop && (
                            <div className="flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                              <span>Drop: {shoe.specifications.drop}mm</span>
                            </div>
                          )}
                          {shoe.terrain.length > 0 && (
                            <div className="flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                              <span className="capitalize">{shoe.terrain.join(', ')}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side: Price Cards (1 column) */}
              <div className="lg:col-span-1 grid grid-cols-2 lg:grid-cols-1 gap-2 sm:gap-4">

                {/* Men's Lowest Price Today Card */}
                <div className="border border-gray-200 rounded-lg p-2 sm:p-4">
                  <div className="text-[10px] sm:text-sm text-gray-600 mb-0.5 sm:mb-1">Men's Lowest</div>
                  <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-2 mb-1.5 sm:mb-2">
                    <span className="text-lg sm:text-2xl font-bold text-green-600">
                      {formatPrice(shoe.price.current)}
                    </span>
                    {shoe.price.original && shoe.price.original > shoe.price.current && (
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] sm:text-sm text-gray-400 line-through">
                          {formatPrice(shoe.price.original)}
                        </span>
                        <span className="bg-red-500 text-white px-1 sm:px-2 py-0.5 rounded text-[9px] sm:text-xs font-bold">
                          -{calculateDiscount(shoe.price.current, shoe.price.original)}%
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1.5 sm:gap-2">
                    {hasPermission && shoe.availability.url && shoe.availability.url !== '' && shoe.availability.url !== '#' ? (
                      <a
                        href={shoe.availability.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 bg-orange-600 hover:bg-orange-700 active:bg-orange-800 text-white font-semibold py-1.5 sm:py-2 px-2 sm:px-4 rounded-lg transition-colors text-[10px] sm:text-sm flex items-center justify-center"
                        style={{ textDecoration: 'none' }}
                      >
                        Buy Now
                      </a>
                    ) : (
                    <button
                      onClick={() => handleBuyNow(shoe.availability.url)}
                        className="flex-1 bg-orange-600 hover:bg-orange-700 active:bg-orange-800 text-white font-semibold py-1.5 sm:py-2 px-2 sm:px-4 rounded-lg transition-colors text-[10px] sm:text-sm"
                    >
                      Buy Now
                    </button>
                    )}
                    <button
                      onClick={() => handleFavoriteClick('men')}
                      disabled={favoriteLoading === 'men'}
                      className={`flex-shrink-0 p-1.5 sm:p-2 border border-gray-300 rounded-lg transition-colors group ${favoriteLoading === 'men'
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:bg-gray-50 hover:border-red-300'
                        }`}
                      title="Add to Favorites & Track Price"
                    >
                      <Heart className={`w-3.5 h-3.5 sm:w-5 sm:h-5 transition-colors ${isMenFavorite
                          ? 'text-red-500 fill-red-500'
                          : 'text-gray-400 group-hover:text-red-500'
                        }`} />
                    </button>
                  </div>
                </div>

                {/* Women's Lowest Price Today Card */}
                <div className="border border-gray-200 rounded-lg p-2 sm:p-4">
                  <div className="text-[10px] sm:text-sm text-gray-600 mb-0.5 sm:mb-1">Women's Lowest</div>
                  <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-2 mb-1.5 sm:mb-2">
                    <span className="text-lg sm:text-2xl font-bold text-green-600">
                      {formatPrice(shoe.price.womenCurrent || shoe.price.current)}
                    </span>
                    {shoe.price.original && (
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] sm:text-sm text-gray-400 line-through">
                          {formatPrice(shoe.price.original)}
                        </span>
                        <span className="bg-red-500 text-white px-1 sm:px-2 py-0.5 rounded text-[9px] sm:text-xs font-bold">
                          -{shoe.price.womenDiscount || calculateDiscount(shoe.price.womenCurrent || shoe.price.current, shoe.price.original)}%
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1.5 sm:gap-2">
                    {hasPermission && (shoe.availability.womenUrl || shoe.availability.url) && (shoe.availability.womenUrl || shoe.availability.url) !== '' && (shoe.availability.womenUrl || shoe.availability.url) !== '#' ? (
                      <a
                        href={shoe.availability.womenUrl || shoe.availability.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 bg-orange-600 hover:bg-orange-700 active:bg-orange-800 text-white font-semibold py-1.5 sm:py-2 px-2 sm:px-4 rounded-lg transition-colors text-[10px] sm:text-sm flex items-center justify-center"
                        style={{ textDecoration: 'none' }}
                      >
                        Buy Now
                      </a>
                    ) : (
                    <button
                      onClick={() => handleBuyNow(shoe.availability.womenUrl || shoe.availability.url)}
                        className="flex-1 bg-orange-600 hover:bg-orange-700 active:bg-orange-800 text-white font-semibold py-1.5 sm:py-2 px-2 sm:px-4 rounded-lg transition-colors text-[10px] sm:text-sm"
                    >
                      Buy Now
                    </button>
                    )}
                    <button
                      onClick={() => handleFavoriteClick('women')}
                      disabled={favoriteLoading === 'women'}
                      className={`flex-shrink-0 p-1.5 sm:p-2 border border-gray-300 rounded-lg transition-colors group ${favoriteLoading === 'women'
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:bg-gray-50 hover:border-red-300'
                        }`}
                      title="Add to Favorites & Track Price"
                    >
                      <Heart className={`w-3.5 h-3.5 sm:w-5 sm:h-5 transition-colors ${isWomenFavorite
                          ? 'text-red-500 fill-red-500'
                          : 'text-gray-400 group-hover:text-red-500'
                        }`} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Price History Chart */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-2.5 sm:p-6 mb-3 sm:mb-6 border border-gray-100">
            <div className="flex items-center justify-between gap-2 mb-2 sm:mb-4">
              <h2 className="text-sm sm:text-xl font-bold text-gray-900">📊 Price History</h2>

              <div className="flex items-center gap-1.5 sm:gap-4">
                {/* Legend */}
                <div className="flex items-center gap-2 sm:gap-4 text-[10px] sm:text-sm">
                  <div className="flex items-center gap-0.5 sm:gap-2">
                    <div className="w-1.5 h-1.5 sm:w-3 sm:h-3 bg-blue-500 rounded-full"></div>
                    <span className="text-gray-600">Men</span>
                  </div>
                  <div className="flex items-center gap-0.5 sm:gap-2">
                    <div className="w-1.5 h-1.5 sm:w-3 sm:h-3 bg-pink-500 rounded-full"></div>
                    <span className="text-gray-600">Women</span>
                  </div>
                </div>

                {/* Time Range Selector */}
                <div className="flex items-center gap-0.5 bg-gray-100 rounded-md sm:rounded-lg p-0.5">
                  {(['15d', '1m', '3m', '1y'] as const).map((range) => (
                    <button
                      key={range}
                      onClick={() => setTimeRange(range)}
                      className={`px-1.5 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-sm font-medium rounded transition-all ${timeRange === range
                          ? 'bg-white text-orange-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                      {range}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Chart Canvas */}
            <div className="relative h-36 sm:h-64">
              <Line data={chartData} options={chartOptions as any} />
            </div>
          </div>

          {/* Shoe Details */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-2.5 sm:p-6 mb-3 sm:mb-6 border border-gray-100">
            <h2 className="text-sm sm:text-xl font-bold text-gray-900 mb-2 sm:mb-4">📋 Shoe Details</h2>

            <div className="grid grid-cols-2 md:grid-cols-2 gap-x-2 sm:gap-x-8 gap-y-1 sm:gap-y-3">
              {shoe.detailedSpecs?.pace && (
                <div className="flex justify-between py-1.5 sm:py-2 border-b border-gray-100">
                  <span className="text-xs sm:text-sm text-gray-600">Category</span>
                  <span className="text-xs sm:text-sm font-semibold text-gray-900">{shoe.detailedSpecs.pace}</span>
                </div>
              )}
              {shoe.specifications.weight && (
                <div className="flex justify-between py-1.5 sm:py-2 border-b border-gray-100">
                  <span className="text-xs sm:text-sm text-gray-600">Weight</span>
                  <span className="text-xs sm:text-sm font-semibold text-gray-900">{shoe.specifications.weight} oz / {Math.round(shoe.specifications.weight * 28.35)}g</span>
                </div>
              )}
              {shoe.specifications.drop && (
                <div className="flex justify-between py-1.5 sm:py-2 border-b border-gray-100">
                  <span className="text-xs sm:text-sm text-gray-600">Drop</span>
                  <span className="text-xs sm:text-sm font-semibold text-gray-900">{shoe.specifications.drop} mm</span>
                </div>
              )}
              {shoe.specifications.stability && (
                <div className="flex justify-between py-1.5 sm:py-2 border-b border-gray-100">
                  <span className="text-xs sm:text-sm text-gray-600">Arch Support</span>
                  <span className="text-xs sm:text-sm font-semibold text-gray-900 capitalize">{shoe.specifications.stability}</span>
                </div>
              )}
              {shoe.detailedSpecs?.strikePattern && (
                <div className="flex justify-between py-1.5 sm:py-2 border-b border-gray-100">
                  <span className="text-xs sm:text-sm text-gray-600">Strike Pattern</span>
                  <span className="text-xs sm:text-sm font-semibold text-gray-900">{shoe.detailedSpecs.strikePattern}</span>
                </div>
              )}
              {shoe.detailedSpecs?.fit && (
                <div className="flex justify-between py-1.5 sm:py-2 border-b border-gray-100">
                  <span className="text-xs sm:text-sm text-gray-600">Fit</span>
                  <span className="text-xs sm:text-sm font-semibold text-gray-900">{shoe.detailedSpecs.fit}</span>
                </div>
              )}
              {shoe.specifications.cushioning && (
                <div className="flex justify-between py-1.5 sm:py-2 border-b border-gray-100">
                  <span className="text-xs sm:text-sm text-gray-600">Midsole</span>
                  <span className="text-xs sm:text-sm font-semibold text-gray-900 capitalize">{shoe.specifications.cushioning}</span>
                </div>
              )}
              {shoe.detailedSpecs?.widthsAvailable && (
                <div className="flex justify-between py-1.5 sm:py-2 border-b border-gray-100">
                  <span className="text-xs sm:text-sm text-gray-600">Widths Available</span>
                  <span className="text-xs sm:text-sm font-semibold text-gray-900">{shoe.detailedSpecs.widthsAvailable}</span>
                </div>
              )}
              {shoe.detailedSpecs?.removableInsole !== null && shoe.detailedSpecs?.removableInsole !== undefined && (
                <div className="flex justify-between py-1.5 sm:py-2 border-b border-gray-100">
                  <span className="text-xs sm:text-sm text-gray-600">Removable Insole</span>
                  <span className="text-xs sm:text-sm font-semibold text-gray-900">
                    {(shoe.detailedSpecs.removableInsole === true || shoe.detailedSpecs.removableInsole === 'True') ? '✅ Yes' : '❌ No'}
                  </span>
                </div>
              )}
              {shoe.detailedSpecs?.breathability && (
                <div className="flex justify-between py-1.5 sm:py-2 border-b border-gray-100">
                  <span className="text-xs sm:text-sm text-gray-600">Breathability</span>
                  <span className="text-xs sm:text-sm font-semibold text-gray-900">{shoe.detailedSpecs.breathability}</span>
                </div>
              )}
            </div>
          </div>

        </main>

        {/* Authentication Modal */}
        <QuickAuthModal
          isOpen={showAuthModal}
          onClose={handleAuthClose}
          onSuccess={handleAuthSuccess}
          title="Sign in to continue"
        />

        {/* Premium Upgrade Modal */}
        <PremiumUpgradeModal
          isOpen={showPremiumModal}
          onClose={() => {
            setShowPremiumModal(false);
            setPendingPurchaseUrl(null);
          }}
          onSuccess={() => {
            setShowPremiumModal(false);
            // After successful upgrade, open the purchase link
            if (pendingPurchaseUrl) {
              window.open(pendingPurchaseUrl, '_blank', 'noopener,noreferrer');
              setPendingPurchaseUrl(null);
            }
          }}
          selectedShoeId={shoe?._id}
          selectedShoeName={shoe ? `${shoe.brand} ${shoe.name}` : undefined}
        />
      </div>
    </>
  )
}

export default ShoePage
